import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import { WEEK_HOURS } from 'Features/SchedulerView/ClipToWeek'

/**
 * Single source of truth for the Portal's makespan.
 *
 * **Makespan is the last hour of PRODUCTION activity** — the latest end across
 * every kitchen batch and line run, ignoring the clean-up tail. That is the
 * definition the factory-schedule KPI tile has always used
 * (`FactoryScheduleView.view.tsx`, max end of the production bars), and as of
 * v7.73.0 it is the definition everywhere: this helper, the week tiles, the
 * answer drop-down, and what `scheduler_api` reports for a run.
 *
 * It did NOT used to be. This helper previously mirrored the engine's
 * `objectives::last_activity_end` — production *plus* every trailing CIP and
 * changeover, i.e. "when is the plant clear?" — which is a different quantity
 * and on a real schedule sits up to 16h higher (see `Scheduler/CLAUDE.md`
 * §"Three makespan quantities"). Both are legitimate numbers; showing one
 * under a label the rest of the screen uses for the other is what made a
 * planner distrust the figure, so the Portal now shows one of them and shows
 * it consistently.
 *
 * The clean-up tail is still visible where it belongs — the Gantt draws every
 * CIP and changeover, and the clean-count figures beside the makespan are what
 * quantify it. It just no longer moves the makespan number.
 *
 * Consequence worth knowing when comparing against history: any makespan
 * recorded before this change, and any stored backend stat on an iteration
 * last run before it, is the plant-clear quantity and will read higher than
 * the same schedule does now. The engine's own tuning notes are all in
 * plant-clear terms too.
 */

/** Latest PRODUCTION end contributed by one row (kitchen batch or line run). */
export const rowProductionEnd = (row: InputRow): number => {
  let last = 0
  for (const value of [row.line_end_time, row.kitchen_end_time]) {
    if (typeof value === 'number' && Number.isFinite(value) && value > last) {
      last = value
    }
  }
  return last
}

/**
 * Earliest PRODUCTION start on one row, or `Infinity` when it has none.
 *
 * `Infinity` rather than 0 for the empty case, deliberately: the only caller
 * uses this to ask "does this row's production reach into the week at all?",
 * and a 0 default would answer yes for every row that has no production to
 * place. Compare with `rowProductionEnd`, whose 0 default is safe because it
 * is looking for a maximum.
 */
export const rowProductionStart = (row: InputRow): number => {
  let first = Infinity
  for (const value of [row.line_start_time, row.kitchen_start_time]) {
    if (typeof value === 'number' && Number.isFinite(value) && value < first) {
      first = value
    }
  }
  return first
}

/**
 * Schedule makespan = the last hour of production activity. Returns 0 for an
 * empty row set.
 *
 * Pass the FULL row set for the schedule. Passing a filtered subset yields the
 * makespan of that subset, which is not the schedule's makespan — that
 * conflation is what made the KPI tile disagree with the week tile.
 */
export const computeMakespan = (rows: InputRow[]): number => {
  let last = 0
  for (const row of rows) {
    const end = rowProductionEnd(row)
    if (end > last) last = end
  }
  return last
}

/**
 * Makespan of ONE week of a consolidated multi-week run.
 *
 * Those rows carry ABSOLUTE consolidated-horizon hours, so `computeMakespan`
 * on them reports where the row ends in the whole run — week 2 of a 2-week
 * solve reads 185h rather than the 17h of that week it actually occupies.
 * This measures how far production extends INTO the given slice instead,
 * clipped at the slice end: a run at absolute 140–210 reports 168h in slice
 * 0 (it fills the week) and 42h in slice 1.
 *
 * **A row is bucketed into a week by its whole activity span, cleaning
 * included, so being in this week's row set does NOT mean its production is.**
 * That is the case the overlap guard below exists for, and it is not a corner
 * case: a batch that starts a week is routinely preceded by an idle preclean
 * in the week before, which puts the row in both. Clipping such a row's
 * production end to the slice boundary without the guard turns "produces
 * nothing this week" into "produces right up to hour 168" — on schedule
 * 31692634253 that made week 1 read 168h against its real 29h, because a
 * PKN12 batch running 168–204 was pulled into week 1 by its 157–168 preclean.
 *
 * Deliberately the same rule as `scheduler_api`'s `slice_makespan`
 * (`azure_endpoint.rs`, the per-slice write), so a tile agrees with what the
 * run recorded — the guard was added on both sides together.
 *
 * Only for consolidated rows — a single-week schedule legitimately runs past
 * hour 168, and clipping it there would under-report. Use `computeMakespan`
 * for those.
 */
export const computeSliceMakespan = (
  rows: InputRow[],
  weekBaseHour: number
): number => {
  const sliceEnd = weekBaseHour + WEEK_HOURS
  let last = 0
  for (const row of rows) {
    const prodEnd = rowProductionEnd(row)
    // Production entirely outside this slice contributes nothing — neither
    // the row that has not started producing yet nor the one that finished
    // in an earlier week. Both reach here via the cleaning half of their
    // activity span.
    if (rowProductionStart(row) >= sliceEnd || prodEnd <= weekBaseHour) continue
    const end = Math.min(prodEnd, sliceEnd) - weekBaseHour
    if (end > last) last = end
  }
  return last
}

/**
 * The makespan to show for ONE selected week — the entry point every
 * week-scoped readout should use.
 *
 * `weekBaseHour` is `weekBaseHourFor`'s result: `null` for a single-week
 * schedule (whose rows are already 0–168 and must not be clipped), `0` for
 * week 1 of a consolidated run, `168` for week 2, and so on. Those two cases
 * are not the same and conflating them is the whole reason `weekBaseHourFor`
 * returns `null` rather than `0`.
 *
 * The distinction matters because a consolidated run's rows carry ABSOLUTE
 * horizon hours on EVERY week's fetch. Without this, week 2 of a two-week
 * solve reports where its last run ends in the whole horizon — 263h — rather
 * than the 95h of that week it actually occupies. The week tiles have done it
 * this way since consolidated runs landed; this is what lets the stat cards,
 * the control panel and the comparison dialog agree with them.
 */
export const makespanForWeek = (
  rows: InputRow[],
  weekBaseHour: number | null
): number =>
  weekBaseHour === null
    ? computeMakespan(rows)
    : computeSliceMakespan(rows, weekBaseHour)
